// apps/web/src/lib/kanban/filtros.ts — Fase 11, Plano 17 (KAN-02, D-28, D-33).
// Filtros do quadro: só escondem cartões na tela. Contagens de coluna e WIP continuam vindo do servidor.
import type { KanbanCartao, KanbanQuadro, NivelPrioridadeKanban } from '@pieltec/contracts';
import { rotuloBanho, rotuloTinta } from '../material-atributos';

export interface FiltrosKanban {
  cliente: string | null;
  prioridades: NivelPrioridadeKanban[];
  /** Rótulos de subtipo: AÇO/ALU/JATO (banho) e PÓ/LÍQ/PTFE (tinta). */
  subtipos: string[];
  celulas: string[];
}

export interface OpcoesFiltroKanban {
  clientes: string[];
  celulas: string[];
}

export const SUBTIPOS_BANHO = ['AÇO', 'ALU', 'JATO'] as const;
export const SUBTIPOS_TINTA = ['PÓ', 'LÍQ', 'PTFE'] as const;
export const PRIORIDADES_KANBAN: readonly NivelPrioridadeKanban[] = ['urgente', 'alta', 'normal'];

export function filtrosVazios(): FiltrosKanban {
  return { cliente: null, prioridades: [], subtipos: [], celulas: [] };
}

export function filtrosAtivos(filtros: FiltrosKanban): boolean {
  return (
    filtros.cliente !== null ||
    filtros.prioridades.length > 0 ||
    filtros.subtipos.length > 0 ||
    filtros.celulas.length > 0
  );
}

export function chaveFiltros(userId: string): string {
  return `kanban.filtros.${userId}`;
}

/** Minúsculas e sem acentos, para comparar nomes de cliente. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function subtiposDoCartao(cartao: KanbanCartao): string[] {
  return [rotuloBanho(cartao.tipoBanho), rotuloTinta(cartao.tipoTinta)].filter(
    (rotulo): rotulo is string => rotulo !== null,
  );
}

function cartaoPassa(cartao: KanbanCartao, filtros: FiltrosKanban, celulaDaRaia: string | null): boolean {
  if (filtros.cliente !== null && normalizar(cartao.clienteNome) !== normalizar(filtros.cliente)) return false;
  if (filtros.prioridades.length > 0 && !filtros.prioridades.includes(cartao.nivelPrioridade)) return false;
  if (filtros.subtipos.length > 0) {
    const subtipos = subtiposDoCartao(cartao);
    if (!filtros.subtipos.some((subtipo) => subtipos.includes(subtipo))) return false;
  }
  if (filtros.celulas.length > 0) {
    const celula = cartao.celulaCodigo ?? celulaDaRaia;
    if (celula === null || !filtros.celulas.includes(celula)) return false;
  }
  return true;
}

/**
 * Esconde cartões que não atendem aos filtros (E entre categorias, OU dentro de cada uma).
 * Imutável: colunas e raias permanecem, e `totalCartoes`/ocupações seguem os valores do servidor (D-28).
 */
export function aplicarFiltros(quadro: KanbanQuadro, filtros: FiltrosKanban): KanbanQuadro {
  if (!filtrosAtivos(filtros)) return quadro;
  return {
    ...quadro,
    colunas: quadro.colunas.map((coluna) => ({
      ...coluna,
      raias: coluna.raias.map((raia) => ({
        ...raia,
        cartoes: raia.cartoes.filter((cartao) => cartaoPassa(cartao, filtros, raia.celulaCodigo)),
      })),
    })),
  };
}

function ordenar(valores: Iterable<string>): string[] {
  return [...valores].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
}

/** Clientes (uma grafia por nome normalizado) e células presentes no quadro, em ordem alfabética. */
export function opcoesDeFiltro(quadro: KanbanQuadro): OpcoesFiltroKanban {
  const clientes = new Map<string, string>();
  const celulas = new Set<string>();
  for (const coluna of quadro.colunas) {
    for (const raia of coluna.raias) {
      if (raia.celulaCodigo) celulas.add(raia.celulaCodigo);
      for (const cartao of raia.cartoes) {
        const chave = normalizar(cartao.clienteNome);
        if (!clientes.has(chave)) clientes.set(chave, cartao.clienteNome);
        if (cartao.celulaCodigo) celulas.add(cartao.celulaCodigo);
      }
    }
  }
  return { clientes: ordenar(clientes.values()), celulas: ordenar(celulas) };
}
